import { mapObjIndexed } from 'ramda';
import { selector, selectorFamily, } from 'recoil';
import { isValueEmpty } from './values';
/**
 * validate a value against a schema and return a structured result
 * @param schema  the schema
 * @param value the value you want to validate
 * @param context validation options
 * @param checkAnyObjectField if true an object is considered empty if one of its fields is undefined or null (default true)
 * @returns a validation result
 */
export function validateSchema(schema, value, { validateOptions, checkAnyObjectField, customIsValueEmptyFn, } = {}) {
    // value not defined yet, do not show errors in fields yet (but still validate!)
    const empty = customIsValueEmptyFn
        ? customIsValueEmptyFn(value)
        : isValueEmpty(value, checkAnyObjectField);
    // do the validation (empty fields might be valid, e.g. non-required fields)
    try {
        schema.validateSync(value, validateOptions);
        return { valid: true, empty, errors: [] };
    }
    catch (e) {
        if (e instanceof Error && e.name === 'ValidationError') {
            const validationError = e;
            return { valid: false, empty, errors: validationError.errors };
        }
        // unhandled error from yup
        const message = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
        console.error('Unhandled yup error', e);
        return { valid: false, empty, errors: [message] };
    }
}
export const alwaysValidValidationSelector = selector({
    key: 'validation/alwaysValid',
    get: () => alwaysValid(null),
});
/** make sure all validation result objects are valid */
export function allValid(resultsObj) {
    const results = Object.values(resultsObj);
    // check if all results are valid
    const valid = results.reduce((acc, res) => {
        if (!res.valid)
            return false;
        return acc;
    }, true);
    // check if all results are empty
    const empty = results.reduce((acc, res) => {
        if (!res.empty)
            return false;
        return acc;
    }, true);
    // collect errors from results
    const errors = mapObjIndexed((res) => res.errors, resultsObj);
    return { valid, empty, errors };
}
export function isMultipleValidationResultsErrors(element) {
    return !Array.isArray(element);
}
/** Count in recursive error object */
function countErrors(errors) {
    let amount = 0;
    Object.entries(errors).map(([, error]) => {
        amount += isMultipleValidationResultsErrors(error) // check if its nested
            ? countErrors(error)
            : error.length === 0
                ? 0
                : 1;
    });
    return amount;
}
/**
 * Count how many fields have validation errors
 */
export function countValidationErrors(result) {
    return countErrors(result.errors);
}
/** Create flat map in recursive error object */
function getErrorsMap(errors, eArray) {
    const errorsArray = eArray ?? [];
    Object.entries(errors).map(([key, error]) => {
        if (error.length === 0)
            return;
        // check if its nested
        if (isMultipleValidationResultsErrors(error)) {
            return getErrorsMap(error, errorsArray);
        }
        errorsArray.push([key, error]);
    });
    return errorsArray;
}
/**
 * Get all errors as a flat array [fieldName, error[]]
 */
export function getValidationErrorsFlatArray(result) {
    return getErrorsMap(result.errors);
}
// return a valid object
export function alwaysValid(value) {
    const empty = isValueEmpty(value);
    return { valid: true, errors: [], empty };
}
/** return an error object */
export function backendError(value, errors) {
    const empty = isValueEmpty(value);
    return { valid: false, errors, empty, backendError: true };
}
/**
 * Create a fieldHelper to create validation selectors. baseKey is prefixed to all selectors keys.
 * @param baseKey
 * @returns
 */
export const createFieldValidationSelector = (baseKey) => 
/**
 * Create a validation selector by providing a selector to get the value and a yup schema to validate it
 * @param fieldName
 * @param recoilSelector
 * @param yupSchema
 * @param dependencySelector used if the selected state is dependent on other state
 * @param id
 * @returns
 */
(fieldName, recoilSelector, yupSchema, dependencySelector, id) => selector({
    key: `${baseKey}/${String(fieldName)}/${recoilSelector.key}/${id}`,
    get: ({ get }) => {
        const value = get(recoilSelector);
        if (dependencySelector) {
            const isRequired = get(dependencySelector);
            return isRequired
                ? validateSchema(yupSchema, value)
                : alwaysValid(true);
        }
        return validateSchema(yupSchema, value);
    },
});
/**
 * Create a fieldHelper to create validation selectorsFamilies. baseKey is prefixed to all selectors keys.
 * @param baseKey
 * @returns
 */
export const createFieldValidationSelectorFamily = (baseKey) => 
/**
 * Create a validation selector by providing a selectorFamily to get the value and a yup schema to validate it
 * @param fieldName
 * @param recoilSelectorFamily
 * @param yupSchema
 * @returns
 */
(fieldName, recoilSelectorFamily, yupSchema) => selectorFamily({
    key: `${baseKey}/${String(fieldName)}/${recoilSelectorFamily.name}`,
    get: (params) => ({ get }) => {
        const value = get(recoilSelectorFamily(params));
        return validateSchema(yupSchema, value);
    },
});
/** Group validation selectors into e.g. a Step Validation Selector to validate multiple fields of a page at once */
export const createMultipleFieldValidationSelector = (key, selectors) => selector({
    key,
    get: ({ get }) => 
    // we list all selectors as key/value pairs here, where the key is always
    // the same as the field, this makes it easy to jump to the corresponding refs later on
    allValid(mapObjIndexed((value) => get(value), selectors)),
});
/** Validate a group of selectors based on a set value from e.g. a select field, see CreateInsuranceConfirmationDialogContent.tsx  *
 *
 * @param: key
 * @param: dependencySelector the selector that saves the value which validationSelector should be used
 * @param: selectors the collection of multipleFieldValidationSelectors where the key is the value of the dependencySelector
 * @param: baseValidationSelector describes a validationSelector that should always be triggered, no matter the dependency
 *
 */
export const createMultipleDependendFieldValidationSelector = (key, dependencySelector, selectors, baseValidationSelector) => selector({
    key,
    get: ({ get }) => {
        const dependencyValue = get(dependencySelector);
        if (baseValidationSelector) {
            return allValid({
                baseValidationSelector: get(baseValidationSelector),
                dependencySelectors: get(selectors[dependencyValue]),
            });
        }
        return selectors[dependencyValue];
    },
});
